import { useEffect, useState, useRef } from "react";
import { useDispatch } from "react-redux";
import { AppDispatch } from "configureStore";
import { Button, InlineStack, Banner, Select } from "@shopify/polaris";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { setDestroy, setIsModalxOpen } from "components/modalx/modalx.slice";
import { uploadToDokobit } from "services/upload-file.service";
import { fetchFileDetails } from "containers/file/file.Slice";
import MultipleUserSelect from "features/multi-user-select/multi-user-select";
import CustomerContactMultiSelect from "features/customer-contact-select/CustomerContactMultiSelect";
import { uploadClickToSign } from "services/clicktosign.service";
import { fetchFileCategorySignatureTypes } from "services/files-category.service";
import SignontabSelect from "features/signontab-select/SignontabSelect";
import { uploadToSignontab } from "services/signontab.service";
import { signerEditErrorKey } from "utils/signer-edit-errors";

interface ConfirmModalProps {
  companyId: string;
  fileId?: string;
  usersList?: string[];
  customerId?: string;
  customerIds?: string[];
  customers?: { id: string; name?: string }[];
  contactSigners?: { id: string; customer?: string; name?: string }[];
  parentFileId?: string;
  signType?: string;
  tabUser?: string;
  // The document's own category — decides which signing methods are offered.
  // Passed when the modal is opened for a row on another document's page
  // (the URL then carries the parent's category, not this document's).
  categoryId?: string;
  // Owner's signing rule: on a template-generated document, signers already
  // invited may not be removed here — only added — since the backend accepts
  // additions but rejects click-to-sign/assign (and the equivalent Dokobit
  // re-upload) when it would drop an existing signer/contact. Withdrawing the
  // whole document is the only way to undo it.
  isTemplateGenerated?: boolean;
  // Runs after the request was sent, e.g. so a list outside redux can refresh.
  onSuccess?: () => void;
}

interface SignatureTypeEntity {
  id: string;
  label: string; // eg. "dokobit"
  value: string; // eg. "Dokobit"
  title: string;
}

// Sign types a customer contact can sign with, in order of preference —
// Click-to-sign is for employees only (the backend drops Contacts rows).
const CONTACT_SIGN_TYPES = ["Dokobit", "Signontab"];

const ConfirmModal: React.FC<ConfirmModalProps> = ({
  companyId,
  fileId,
  usersList,
  customerId,
  customers = [],
  contactSigners: contactSignersProps = [],
  parentFileId,
  signType: signTypeProps,
  tabUser,
  categoryId: categoryIdProps,
  isTemplateGenerated = false,
  onSuccess,
}) => {
  const dispatch: AppDispatch = useDispatch();
  const { catId } = useParams();
  const categoryId = categoryIdProps || catId;
  const { t } = useTranslation();
  // Frozen at mount (this component is remounted fresh each time the modal
  // opens) — the signers/contacts already on the request when editing began,
  // never removable from here on a template-generated document. `usersList`
  // is already a plain array of user ids (the caller ran it through
  // getNonDisabledUserIds before dispatching this modal's props).
  const lockedUserIdsRef = useRef<string[]>(
    isTemplateGenerated ? usersList || [] : []
  );
  const lockedContactIdsRef = useRef<string[]>(
    isTemplateGenerated
      ? ((contactSignersProps || [])
          .map((s) => s?.id)
          .filter(Boolean) as string[])
      : []
  );
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errMsg, setErrMsg] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [users, setUsers] = useState<any>(usersList);
  const [contactSignerIds, setContactSignerIds] = useState<string[]>([]);
  const [contactSignersByCustomer, setContactSignersByCustomer] = useState<
    Record<string, string[]>
  >({});
  const [signType, setSignType] = useState("");
  const [availableSignTypes, setAvailableSignTypes] = useState<
    SignatureTypeEntity[]
  >([]);
  const [selectedTabUser, setSelectedTabUser] = useState<string>("");

  const clearContacts = () => {
    setContactSignerIds([]);
    setContactSignersByCustomer((prev) =>
      Object.fromEntries(Object.keys(prev).map((key) => [key, []]))
    );
  };

  const customersList = customers && customers.length ? customers : [];
  const isMultiCustomer = customersList.length > 1;
  const isClickSignType = signType === "Click";
  // Click cannot carry contacts at all (the backend drops Contacts rows), so
  // switching to it always empties the contact selection. On a
  // template-generated document that would silently drop an already-invited,
  // locked contact — which the save then rejects with
  // template_signatures_locked. Block landing on Click at all while that
  // would happen, rather than let it clear a selection the user cannot
  // put back.
  const hasLockedContacts =
    isTemplateGenerated && lockedContactIdsRef.current.length > 0;
  // The first sign type allowed here that customer contacts can sign with.
  const contactSignType = CONTACT_SIGN_TYPES.find((value) =>
    availableSignTypes.some((type) => type.value === value)
  );
  const hasCustomer = isMultiCustomer || Boolean(customerId);
  // Under Click the contacts are still listed (so they can be found at all),
  // as long as picking one can switch to a type that contacts sign with.
  const showContacts =
    hasCustomer && (!isClickSignType || Boolean(contactSignType));
  const singleCustomerContactIds =
    !isMultiCustomer && contactSignersProps.length
      ? (contactSignersProps.map((s) => s?.id).filter(Boolean) as string[])
      : [];

  useEffect(() => {
    if (!isMultiCustomer && singleCustomerContactIds.length) {
      setContactSignerIds(singleCustomerContactIds);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(singleCustomerContactIds), isMultiCustomer]);

  useEffect(() => {
    if (tabUser) {
      setSelectedTabUser(tabUser);
    }
  }, [tabUser]);

  useEffect(() => {
    if (isMultiCustomer) {
      setContactSignerIds([]);
      setContactSignersByCustomer(() => {
        const assignments: Record<string, string[]> = {};
        const signers = contactSignersProps || [];

        // Assign every matching signer to its customer so order of customers
        // or signers never matters, and several contacts per customer survive.
        customersList.forEach(({ id: customerId }) => {
          assignments[customerId] = signers
            .filter((signer) => signer?.customer === customerId && signer?.id)
            .map((signer) => signer.id as string);
        });

        // Single customer + no customer match on any signer: pre-fill when there's no ambiguity
        if (
          customersList.length === 1 &&
          signers.length > 0 &&
          assignments[customersList[0].id].length === 0
        ) {
          assignments[customersList[0].id] = signers
            .map((s) => s?.id)
            .filter(Boolean) as string[];
        }

        return assignments;
      });
    } else {
      setContactSignersByCustomer({});
    }
  }, [
    isMultiCustomer,
    JSON.stringify(customersList),
    JSON.stringify(contactSignersProps),
  ]);

  /**
   * Fetch signature types allowed for this file/category
   */
  useEffect(() => {
    const fetchSignatureTypes = async () => {
      if (!companyId || !fileId) return;
      try {
        const { signatureTypes }: { signatureTypes: SignatureTypeEntity[] } =
          await fetchFileCategorySignatureTypes(companyId, categoryId);

        const normalised = (signatureTypes || [])
          .filter(Boolean)
          .map((s: any) => {
            return {
              label: s.label,
              value: s.title,
              id: s.id,
              title: s.title,
            };
          })
          // A category can hold the same sign type several times over
          // (duplicate link rows) — list each one once.
          .filter(
            (type: SignatureTypeEntity, index: number, all: SignatureTypeEntity[]) =>
              all.findIndex((other) => other.value === type.value) === index
          );
        setAvailableSignTypes(normalised);
      } catch (error) {
        console.error("Failed to fetch signature types", error);
      }
    };

    fetchSignatureTypes();
  }, [companyId, categoryId, fileId]);

  /**
   * Decide which sign type should be selected once we know what is available and
   * once we know if the caller (props) already asked for a specific one.
   */
  useEffect(() => {
    if (!availableSignTypes.length) return;

    const incoming = signTypeProps ? signTypeProps : "";

    // Try to find a match by value or label
    const match = availableSignTypes.find(
      (t) => t.value === incoming || t.label === incoming
    );

    // Fall back to the first available option when the caller's type is not
    // offered for this category — but never fall back onto Click while that
    // would drop a locked contact (see hasLockedContacts above); the doc's
    // own current type is Dokobit/Signontab in that case, so prefer it.
    const chosen =
      match
        ? match.value
        : hasLockedContacts
        ? availableSignTypes.find((t) => t.value !== "Click")?.value ??
          availableSignTypes[0].value
        : availableSignTypes[0].value;
    setSignType(chosen);
    // Landing on Click drops any contacts pre-filled from the existing
    // request — same rule as picking Click by hand below.
    if (chosen === "Click" && !hasLockedContacts) {
      clearContacts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableSignTypes, signTypeProps]);

  const handleDokobitFile = async () => {
    try {
      setIsLoading(true);
      const multiCustomerContacts = Array.from(
        new Set(
          customersList.flatMap(({ id }) => contactSignersByCustomer[id] || [])
        )
      );
      const payload = {
        fileId,
        signType,
        users,
        // Click-to-sign cannot sign with external contacts (backend drops Contacts rows)
        contactSigners: isClickSignType
          ? []
          : isMultiCustomer
          ? multiCustomerContacts
          : Array.from(new Set(contactSignerIds)),
        selectedTabUser
      };

      if (signType === "Dokobit") {
        await uploadToDokobit(companyId, payload);
      } else if (signType === "Click") {
        await uploadClickToSign(companyId, payload);
      } else if (signType === "Signontab") {
        await uploadToSignontab(companyId, payload);
      }

      if (fileId && companyId && !parentFileId) {
        dispatch(fetchFileDetails({ fileId, companyId }));
      }

      if (parentFileId) {
        dispatch(fetchFileDetails({ fileId: parentFileId, companyId }));
      }

      onSuccess?.();
      setSuccessMessage("ok");
      setIsLoading(false);
      closeModal();
    } catch (e: any) {
      // Surface the backend's specific code (e.g. template_signatures_locked
      // when this would have removed an existing signer) instead of a bare
      // "error" — the UI already prevents that pick, but a race or an older
      // client can still hit it server-side.
      setErrMsg(signerEditErrorKey(e, e?.response?.data?.message || "error"));
      setIsLoading(false);
    }
  };

  // Picking a contact while Click is selected switches to a sign type the
  // contact can actually sign with — otherwise the pick would be dropped.
  const switchToContactSignType = (previous: string[], next: string[]) => {
    const added = next.some((contactId) => !previous.includes(contactId));
    if (isClickSignType && added && contactSignType) {
      setSignType(contactSignType);
    }
  };

  const handleContactChange = (value: string[]) => {
    switchToContactSignType(contactSignerIds, value);
    setContactSignerIds(value);
  };

  const handleMultiCustomerContactChange = (id: string, value: string[]) => {
    switchToContactSignType(contactSignersByCustomer[id] || [], value);
    setContactSignersByCustomer((prev) => ({
      ...prev,
      [id]: value,
    }));
  };

  // Click cannot carry contacts, so switching to it clears them rather than
  // leaving ticked contacts on screen that would silently not be invited —
  // unless one of those contacts is locked (already invited, template-
  // generated document), in which case Click is disabled in the Select below
  // and this just guards against reaching it any other way.
  const handleSignTypeChange = (value: string) => {
    if (value === "Click" && hasLockedContacts) return;
    setSignType(value);
    if (value === "Click") {
      clearContacts();
    }
  };

  const closeModal = () => {
    dispatch(setIsModalxOpen(false));
    dispatch(setDestroy());
  };

  return (
    <>
      <div className="form-warnings">
        {errMsg && (
          <div>
            <Banner tone="critical">{t(errMsg)}</Banner>
          </div>
        )}
        {successMessage && (
          <div>
            <Banner tone="info">{t(successMessage)}</Banner>
          </div>
        )}
      </div>
      <h1 style={{ textAlign: "center" }}>
        {t("Are_you_sure_you_want_to_request_to_sign_file")}
      </h1>
      <div style={{ marginBottom: "10px" }}>
        {isTemplateGenerated && (
          <p className="mt-2 text-sm text-zinc-500">
            {t("template_signers_locked_hint")}
          </p>
        )}
        <MultipleUserSelect
          onChange={setUsers}
          value={users}
          label={"select_user"}
          lockedValueIds={lockedUserIdsRef.current}
        />
        {showContacts && isClickSignType && contactSignType && (
          <p className="mt-2 text-sm text-zinc-500">
            {t("click_sign_contacts_switch_hint", { type: contactSignType })}
          </p>
        )}
        {showContacts && !isMultiCustomer && customerId && (
          <CustomerContactMultiSelect
            label={"select_contact"}
            onChange={handleContactChange}
            value={contactSignerIds}
            companyId={companyId}
            customerId={customerId}
            isRequired={false}
            lockedIds={lockedContactIdsRef.current}
          />
        )}
        {showContacts &&
          isMultiCustomer &&
          customersList.map(({ id, name }) => (
            <CustomerContactMultiSelect
              key={id}
              label={
                name
                  ? `${t("select_contact")} (${name})`
                  : t("select_contact")
              }
              onChange={(value) => handleMultiCustomerContactChange(id, value)}
              value={contactSignersByCustomer[id] || []}
              companyId={companyId}
              customerId={id}
              isRequired={false}
              lockedIds={lockedContactIdsRef.current}
            />
          ))}
        <Select
          label="Select sign type"
          options={availableSignTypes.map(({ label, value }) => ({
            label,
            value,
            // Visible but not selectable — picking it would silently drop a
            // locked (already-invited) contact, since Click cannot carry any.
            disabled: value === "Click" && hasLockedContacts,
          }))}
          value={signType}
          onChange={handleSignTypeChange}
          disabled={availableSignTypes.length === 0}
        />
        {signType === "Signontab" && (
          <SignontabSelect
            value={selectedTabUser}
            onChange={setSelectedTabUser}
          />
        )}
      </div>

      <InlineStack align="space-between">
        <Button
          onClick={handleDokobitFile}
          loading={isLoading}
          disabled={
            !users.length ||
            (signType === "Signontab" && !selectedTabUser)
          }
        >
          {t("Yes")}
        </Button>

        <Button onClick={closeModal}>{t("No")}</Button>
      </InlineStack>
    </>
  );
};

export default ConfirmModal;
